// ══════════════════════════════════════════════════════════════════════
// PRINT MATERIALS — helpers for the weekly print pack
// (menu card, order card, reheating card, salad card, container stickers)
// ══════════════════════════════════════════════════════════════════════

export const KINDS = [
  { kind: 'menu_card', label: 'Menu card', singleton: true, hint: 'Weekly menu, exact 5 × 7 card' },
  { kind: 'order_card', label: 'Order card', singleton: true, hint: 'How to order next week' },
  { kind: 'reheating_card', label: 'Reheating card', singleton: true, hint: 'Care & keeping instructions' },
  { kind: 'salad_card', label: 'Salad card', singleton: true, hint: 'Jar salad assembly card' },
  { kind: 'sticker', label: 'Sticker', singleton: false, hint: '2 × 1 in container label' },
  { kind: 'other', label: 'Other', singleton: false, hint: 'Anything else in the pack' },
]

export const CARD_KINDS = KINDS.filter((k) => k.singleton)
export const KIND_LABELS = Object.fromEntries(KINDS.map((k) => [k.kind, k.label]))
export const ACCEPTED_FILES = '.png,.jpg,.jpeg,.webp,.pdf,image/png,image/jpeg,image/webp,application/pdf'
export const MAX_FILE_BYTES = 25 * 1024 * 1024

// ── Filename → kind / label (mirrors server/routers/print_materials.py) ──

export function classifyFilename(name, width, height) {
  const lowered = (name || '').toLowerCase()
  if (lowered.includes('sticker') || lowered.includes('label')) return 'sticker'
  if (lowered.includes('menu card') || lowered.includes('menu-card') || lowered.includes('menucard')) return 'menu_card'
  if (lowered.includes('order card') || lowered.includes('order-card') || lowered.includes('ordercard')) return 'order_card'
  if ((lowered.includes('care') && lowered.includes('keeping')) || lowered.includes('reheat') || lowered.includes('instruction')) return 'reheating_card'
  if (lowered.includes('salad')) return 'salad_card'
  if (width && height && Math.abs(width / height - 2) < 0.08) return 'sticker'
  return 'other'
}

export function cleanLabel(filename) {
  let name = (filename || '').split('/').pop()
  name = name.replace(/\.(png|jpe?g|webp|pdf)$/i, '')
  name = name.replace(/@[23]x/gi, '')
  name = name.replace(/\s*\(\d+\)\s*$/, '').replace(/\s*\(\d+\)\s*$/, '')
  name = name.replace(/^\d{1,2}[\s._-]+/, '')
  name = name.replace(/^sticker\s*[—–-]\s*/i, '')
  name = name.replace(/\s*[—–-]\s*final\s*$/i, '')
  name = name.replace(/\s*[—–-]?\s*(\d{1,2}[_/]\d{1,2}|(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\.?\s+\d{1,2}(st|nd|rd|th)?(,\s*\d{4})?)\s*$/i, '')
  name = name.replace(/\s*[—–-]\s*final\s*$/i, '')
  name = name.replace(/\s+/g, ' ').replace(/^[\s\-—–_]+|[\s\-—–_]+$/g, '')
  return name || filename
}

export function defaultLabel(kind, filename) {
  return KIND_LABELS[kind] && KINDS.find((k) => k.kind === kind)?.singleton ? KIND_LABELS[kind] : cleanLabel(filename)
}

export function normalizeName(value) {
  return (value || '').toLowerCase().replace(/[^a-z0-9]+/g, '')
}

export function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function isPdf(asset) {
  return asset?.content_type === 'application/pdf'
}

export function absoluteUrl(path) {
  return new URL(path, window.location.origin).href
}

// Read the pixel size of a picked image so we can spot 2:1 stickers before upload.
export function readImageSize(file) {
  if (!file || !file.type.startsWith('image/')) return Promise.resolve(null)
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => { resolve({ width: img.naturalWidth, height: img.naturalHeight }); URL.revokeObjectURL(url) }
    img.onerror = () => { resolve(null); URL.revokeObjectURL(url) }
    img.src = url
  })
}

// ── Sticker ↔ menu dish matching ─────────────────────────────────────

// Words that vary between the menu name and the sticker without changing the dish.
const FILLER_WORDS = new Set(['collection', 'bundle', 'pack', 'set', 'the', 'a', 'and', 'with', 'of'])

function matchKey(value) {
  return (value || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .split(/[^a-z0-9]+/)
    .filter((word) => word && !FILLER_WORDS.has(word))
    .map((word) => (word.length > 3 && word.endsWith('s') ? word.slice(0, -1) : word))
    .join('')
}

function nameMatches(stickerLabel, dishName) {
  const a = matchKey(stickerLabel)
  const b = matchKey(dishName)
  if (!a || !b) return false
  if (a === b) return true
  // "Wellness Shots" vs "Wellness Shot Bundle", "Date Ball Collection" vs "Date Balls"
  const shorter = a.length <= b.length ? a : b
  const longer = a.length <= b.length ? b : a
  return shorter.length >= 6 && longer.includes(shorter)
}

/**
 * Orders stickers the way the menu lists its dishes, and reports which dishes
 * still have no sticker saved.
 */
export function matchStickersToDishes(stickers, dishes) {
  const remaining = [...stickers]
  const ordered = []
  const missingDishes = []
  for (const dish of dishes || []) {
    const index = remaining.findIndex((s) => nameMatches(s.label, dish.name))
    if (index === -1) {
      missingDishes.push(dish)
    } else {
      ordered.push({ ...remaining[index], dish })
      remaining.splice(index, 1)
    }
  }
  remaining.sort((x, y) => (x.label || '').localeCompare(y.label || ''))
  return { ordered: [...ordered, ...remaining], missingDishes }
}

// ── Printing ─────────────────────────────────────────────────────────
// Every print goes through a hidden same-origin iframe carrying its own
// @page size, so the browser's print dialog opens with the right paper
// (letter or 5 × 7 for cards, 2 × 1 in for a single sticker) and no app chrome.

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]))
}

function buildDocument({ title, css, body }) {
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>
html, body { margin: 0; padding: 0; background: #fff; }
* { box-sizing: border-box; }
img { display: block; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
${css}
</style></head><body>${body}</body></html>`
}

let activeFrame = null

const userAgent = globalThis.navigator?.userAgent || ''
const NEEDS_FRAME_FOCUS = /AppleWebKit/i.test(userAgent) && !/Chrome|Chromium|CriOS|Edg|OPR|Firefox|FxiOS/i.test(userAgent)

export async function waitForPrintImages(images, timeoutMs = 15000) {
  await Promise.all(Array.from(images || []).map((img) => {
    // A cached failure is already `complete` with naturalWidth === 0. Do not
    // attach a listener after its error event has fired or printing waits forever.
    if (img.complete) return null
    return new Promise((done) => {
      const timeout = setTimeout(done, timeoutMs)
      const settled = () => { clearTimeout(timeout); done() }
      img.addEventListener('load', settled, { once: true })
      img.addEventListener('error', settled, { once: true })
    })
  }))
  return Array.from(images || []).filter((img) => !img.naturalWidth)
}

export function printDocument(html) {
  return new Promise((resolve, reject) => {
    if (activeFrame) { activeFrame.remove(); activeFrame = null }
    // Printing has to focus the hidden frame (Safari prints the parent page
    // otherwise), so remember where the keyboard was and hand it back after —
    // or Escape/arrow keys in an open preview dialog would go to the frame.
    const restoreTo = document.activeElement
    const restoreFocus = () => {
      if (restoreTo && restoreTo !== document.body && document.contains(restoreTo) && typeof restoreTo.focus === 'function') restoreTo.focus()
    }
    const iframe = document.createElement('iframe')
    iframe.setAttribute('aria-hidden', 'true')
    iframe.setAttribute('title', 'Print')
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:1px;height:1px;border:0;opacity:0;pointer-events:none;'
    // Chromium hands focus to the frame asynchronously, tens of ms after
    // print() has returned and with no reliable parent-side event — so for a
    // short window after printing, keep taking focus back from the frame.
    const guardFocus = () => {
      const start = performance.now()
      const tick = () => {
        if (document.activeElement === iframe) restoreFocus()
        const elapsed = performance.now() - start
        if (elapsed < 1000 && document.contains(iframe)) setTimeout(tick, elapsed < 250 ? 0 : 16)
      }
      tick()
    }
    let finished = false
    const finish = () => {
      if (finished) return
      finished = true
      if (document.activeElement === iframe) restoreFocus()
      setTimeout(() => {
        const hadFocus = document.activeElement === iframe
        iframe.remove()
        if (activeFrame === iframe) activeFrame = null
        if (hadFocus) restoreFocus()
      }, 1500)
    }
    iframe.onload = async () => {
      const win = iframe.contentWindow
      const doc = iframe.contentDocument
      if (!win || !doc) { finish(); reject(new Error('Print frame could not open')); return }
      const broken = await waitForPrintImages(doc.images)
      if (broken.length) { finish(); reject(new Error(`${broken.length} image${broken.length === 1 ? '' : 's'} could not be loaded for printing`)); return }
      // Fit long preparation names without spilling into the next container label.
      for (const label of doc.querySelectorAll('[data-fit-label]')) {
        let size = 10
        while ((label.scrollHeight > label.clientHeight || label.scrollWidth > label.clientWidth) && size > 4) {
          size -= 0.25
          label.style.fontSize = `${size}pt`
        }
        if (label.scrollHeight > label.clientHeight || label.scrollWidth > label.clientWidth) {
          finish(); reject(new Error('A container label name is too long to print legibly')); return
        }
      }
      win.addEventListener('afterprint', finish, { once: true })
      setTimeout(finish, 120000)
      // Only Safari needs the frame focused to print it (otherwise it prints
      // the parent page). Chromium and Firefox print the frame regardless,
      // and focusing it there just steals the keyboard for a few ms.
      if (NEEDS_FRAME_FOCUS) iframe.focus()
      win.print()
      // Chrome blocks here until the dialog closes; Safari returns at once.
      // Either way the caller is done — cleanup rides on afterprint/timeout.
      guardFocus()
      resolve()
    }
    iframe.srcdoc = html
    activeFrame = iframe
    document.body.appendChild(iframe)
  })
}

/** Use saved meal quantities; unknown demand must never become a guess. */
export function demandCount(value) {
  if (value === null || value === undefined || value === '') return null
  const count = Number(value)
  return Number.isSafeInteger(count) && count >= 0 ? count : null
}

export function printQuantity(base, extras = 5) {
  const count = demandCount(base)
  if (count === null) return null
  return count > 0 ? count + (Number(extras) === 10 ? 10 : 5) : 0
}

/** Only stickers with a real quantity can participate in a combined sheet. */
export function printableStickerSheet(stickers) {
  const imageStickers = (stickers || []).filter((sticker) => sticker && !isPdf(sticker))
  return {
    stickers: imageStickers.filter((sticker) => demandCount(sticker.printQuantity) > 0),
    unavailable: imageStickers.filter((sticker) => demandCount(sticker.printQuantity) === null),
  }
}

export function cardDemand(kind, dishes, orderCount) {
  if (kind !== 'salad_card') return demandCount(orderCount)
  const salads = (dishes || []).filter((dish) => ['salad', 'salad_2'].includes(dish.category))
  const counts = salads.map((dish) => demandCount(dish.meal_count))
  return counts.some((count) => count === null) ? null : counts.reduce((sum, count) => sum + count, 0)
}

/** Resolve a front/back card job without inventing a missing order count. */
export function cardJobQuantity(quantityBasis, dishes, orderCount, manualCopies, extras = 5) {
  if (quantityBasis === 'manual') {
    const copies = demandCount(manualCopies)
    return { base: copies, copies, exact: true }
  }
  const base = quantityBasis === 'orders'
    ? demandCount(orderCount)
    : quantityBasis === 'salads'
      ? cardDemand('salad_card', dishes, orderCount)
      : demandCount((dishes || []).find((dish) => String(dish.dish_id) === String(quantityBasis))?.meal_count)
  return { base, copies: printQuantity(base, extras), exact: false }
}

function copiesCount(value) {
  const count = demandCount(value)
  if (count === null) throw new Error('Print quantity is unavailable. Check the meal or order counts first.')
  return count
}

/** Exact 5 × 7 cards, centered on letter paper or printed on 5 × 7 stock. */
export function cardJobsDocument(jobs, title = 'Print materials', options = {}) {
  const paper = options.paper === 'card' ? 'card' : 'letter'
  const fitMode = options.fitMode === 'contain' ? 'contain' : 'fill'
  const flipMode = options.flipMode === 'short' ? 'short' : 'long'
  const side = ['front', 'back'].includes(options.side) ? options.side : 'duplex'
  const rotateManualBack = flipMode === 'short' && (options.duplexMode === 'manual' || side === 'back')
  const cropMarks = paper === 'letter' && options.cropMarks !== false
  const pageSize = paper === 'card' ? '5in 7in' : 'letter'
  const pageDimensions = paper === 'card' ? 'width: 5in; height: 7in;' : 'width: 8.5in; height: 11in;'
  const bleed = paper === 'letter' && fitMode === 'fill' ? '-0.125in' : '0'
  const css = `
@page { size: ${pageSize}; margin: 0; }
.page { ${pageDimensions} display: flex; align-items: center; justify-content: center; break-after: page; page-break-after: always; overflow: hidden; }
.page:last-child { break-after: auto; page-break-after: auto; }
.card-frame { position: relative; width: 5in; height: 7in; background: #f7f4ec; }
.card-frame img { position: absolute; inset: ${bleed}; width: ${bleed === '0' ? '5in' : '5.25in'}; height: ${bleed === '0' ? '7in' : '7.25in'}; object-fit: ${fitMode === 'contain' ? 'contain' : 'cover'}; }
${rotateManualBack ? '.page[data-side="back"] .card-frame { transform: rotate(180deg); }' : ''}
.crop { position: absolute; width: 0.25in; height: 0.25in; z-index: 2; }
.crop::before, .crop::after { content: ""; position: absolute; }
/* Borders print even when the browser's Background graphics option is off. */
.crop::before { left: 0; bottom: 0; width: 0.2in; border-bottom: 0.75pt solid #000; }
.crop::after { top: 0; right: 0; height: 0.2in; border-right: 0.75pt solid #000; }
.crop.tl { left: -0.25in; top: -0.25in; }
.crop.tr { right: -0.25in; top: -0.25in; transform: rotate(90deg); }
.crop.br { right: -0.25in; bottom: -0.25in; transform: rotate(180deg); }
.crop.bl { left: -0.25in; bottom: -0.25in; transform: rotate(270deg); }`
  let body = ''
  for (const { front, back = null, copies } of jobs) {
    const count = copiesCount(copies)
    if (!count) continue
    if (!front || isPdf(front) || (back && isPdf(back))) throw new Error('Choose PNG, JPG, or WebP artwork for sheet printing. Download PDFs to print separately.')
    if (side === 'back' && !back) throw new Error('Choose back artwork before printing backs.')
    const marks = cropMarks ? '<span class="crop tl"></span><span class="crop tr"></span><span class="crop br"></span><span class="crop bl"></span>' : ''
    const page = (asset, pageSide) => `<section class="page" data-side="${pageSide}" data-flip="${flipMode}"><div class="card-frame"><img src="${escapeHtml(absoluteUrl(asset.file_url))}" alt="${escapeHtml(asset.label)}">${marks}</div></section>`
    const pages = side === 'front' ? page(front, 'front') : side === 'back' ? page(back, 'back') : page(front, 'front') + (back ? page(back, 'back') : '')
    body += pages.repeat(count)
  }
  if (!body) throw new Error('Nothing to print')
  return buildDocument({ title, css, body })
}

export function printCards(assets, title = 'Print materials') {
  const jobs = assets.filter((a) => a && !isPdf(a)).map((front) => ({ front, copies: front.printQuantity === undefined ? 1 : front.printQuantity }))
  return printDocument(cardJobsDocument(jobs, title))
}

/** One 2 × 1 in page per copy — for a label printer or pre-cut label stock. */
export function printStickerLabels(asset, copies = 1) {
  if (!asset || isPdf(asset)) throw new Error('Nothing to print')
  const count = copiesCount(copies)
  if (!count) throw new Error('Nothing to print')
  const css = `
@page { size: 2in 1in; margin: 0; }
.label { width: 2in; height: 1in; overflow: hidden; break-after: page; page-break-after: always; }
.label:last-child { break-after: auto; page-break-after: auto; }
.label img { width: 2in; height: 1in; object-fit: contain; }`
  const one = `<section class="label"><img src="${escapeHtml(absoluteUrl(asset.file_url))}" alt="${escapeHtml(asset.label)}"></section>`
  return printDocument(buildDocument({ title: `${asset.label} sticker`, css, body: one.repeat(count) }))
}

/**
 * Letter sheets, 4 across × 10 down (40 labels, 2 × 1 in each, no gutters
 * so the cuts are straight lines). `copies` repeats every sticker.
 */
export function stickerSheetDocument(stickers, copies = 1) {
  const printable = stickers.filter((a) => a && !isPdf(a))
  if (!printable.length) throw new Error('No stickers to print')
  const cells = []
  for (const sticker of printable) {
    const count = copiesCount(sticker.printQuantity === undefined ? copies : sticker.printQuantity)
    for (let i = 0; i < count; i += 1) cells.push(sticker)
  }
  if (!cells.length) throw new Error('No stickers to print')
  const perSheet = 40
  const sheets = []
  for (let i = 0; i < cells.length; i += perSheet) sheets.push(cells.slice(i, i + perSheet))
  const css = `
@page { size: letter; margin: 0; }
.sheet { width: 8.5in; height: 11in; padding: 0.5in 0.25in; display: grid; grid-template-columns: repeat(4, 2in); grid-auto-rows: 1in; align-content: start; break-after: page; page-break-after: always; overflow: hidden; }
.sheet:last-child { break-after: auto; page-break-after: auto; }
.sheet img { width: 2in; height: 1in; object-fit: contain; }`
  const body = sheets.map((sheet) => `<section class="sheet">${sheet.map((a) => `<img src="${escapeHtml(absoluteUrl(a.file_url))}" alt="${escapeHtml(a.label)}">`).join('')}</section>`).join('')
  return buildDocument({ title: 'Sticker sheet', css, body })
}

export function printStickerSheet(stickers, copies = 1) {
  return printDocument(stickerSheetDocument(stickers, copies))
}

export function stickerSheetCount(stickerCount, copies = 1) {
  return Math.ceil((stickerCount * Math.max(1, Number(copies) || 1)) / 40)
}
